import { formatPhone, normalizePhone } from './bingo.js';

// Text messages from the admin's SMS tab (user decisions 2026-10-01, sms-suunnitelma.md) – the same rules
// as the desktop program AreenaSMS: message length, fields like {nimi}, and recipient lists from a CSV
// file or pasted from Excel.

export const SMS_TEXT_MAX = 1000;
export const SMS_BATCH_MAX = 1000;
export const SMS_NAME_MAX = 60;
export const SMS_TITLE_MAX = 80;
/** Seconds between two messages (the operator should not take the sending for spam). */
export const SMS_GAP_S = 3;
/** History kept this long (user decision 2026-10-01). */
export const SMS_KEEP_DAYS = 7;

const GSM_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM_EXTENDED = '^{}\\[~]|€\f';

export interface SmsMeasure {
  length: number;
  parts: number;
  unicode: boolean;
  perPart: number;
  /** The characters that force the 70-character limit (at most three). */
  special: string[];
}

/**
 * GSM 03.38: 160 characters, 153 per part when split (Finnish ä ö å are in it; € and a few others count
 * twice). Any other character (emojis, the dash –) makes the message UCS-2: 70, 67 per part.
 */
export function measureSms(text: string): SmsMeasure {
  let gsm = 0;
  const special: string[] = [];
  for (const ch of text) {
    if (ch.length === 1 && GSM_BASIC.includes(ch)) gsm++;
    else if (ch.length === 1 && GSM_EXTENDED.includes(ch)) gsm += 2;
    else if (!special.includes(ch) && special.length < 3) special.push(ch);
  }
  if (special.length > 0) {
    const len = text.length; // UTF-16 units, as the phone counts them
    const parts = len === 0 ? 0 : len <= 70 ? 1 : Math.ceil(len / 67);
    return { length: len, parts, unicode: true, perPart: parts <= 1 ? 70 : 67, special };
  }
  const parts = gsm === 0 ? 0 : gsm <= 160 ? 1 : Math.ceil(gsm / 153);
  return { length: gsm, parts, unicode: false, perPart: parts <= 1 ? 160 : 153, special: [] };
}

const FIELD = /\{([^{}\s]{1,40})\}/g;

/** Fills {nimi}, {numero} and list columns (case-insensitive); an unknown field stays as it is. */
export function fillTemplate(template: string, fields: Record<string, string>): string {
  const lower = new Map(Object.entries(fields).map(([k, v]) => [k.toLowerCase(), v]));
  return template.replace(FIELD, (m, key: string) => lower.get(key.toLowerCase()) ?? m);
}

export function fieldsIn(template: string): string[] {
  const seen = new Map<string, string>();
  for (const m of template.matchAll(FIELD)) if (!seen.has(m[1]!.toLowerCase())) seen.set(m[1]!.toLowerCase(), m[1]!);
  return [...seen.values()];
}

export interface SmsRecipient {
  phone: string;
  name: string;
  fields: Record<string, string>;
}
export interface SmsImport {
  recipients: SmsRecipient[];
  rejected: { line: number; text: string; reason: string }[];
  columns: string[];
}

const PHONE_HEADERS = ['numero', 'puhelin', 'puhelinnumero', 'puh', 'gsm', 'phone', 'mobile', 'matkapuhelin'];
const NAME_HEADERS = ['nimi', 'name', 'etunimi', 'nimimerkki'];

function detectSeparator(lines: string[]): string {
  const sample = lines.filter((l) => l.trim()).slice(0, 10);
  for (const sep of ['\t', ';', ',']) if (sample.length > 0 && sample.every((l) => l.includes(sep))) return sep;
  return sample.some((l) => l.includes('\t')) ? '\t' : sample.some((l) => l.includes(';')) ? ';' : ',';
}

/** One row; quotes as in CSV ("a;b" and "" for a quote). */
function splitRow(line: string, sep: string): string[] {
  const cells: string[] = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i]!;
    if (quoted) {
      if (c === '"' && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (c === '"') quoted = false;
      else cur += c;
    } else if (c === '"' && cur.length === 0) quoted = true;
    else if (c === sep) {
      cells.push(cur);
      cur = '';
    } else cur += c;
  }
  cells.push(cur);
  return cells;
}

/**
 * Recipients from a CSV file or text pasted from Excel: the separator (tab, ; or ,) is detected; a header row
 * naming a phone column ("numero", "puhelin" …) gives the field names; without one the first column with a
 * phone number is the number and the next text column the name. Duplicates are dropped.
 */
export function parseRecipients(text: string): SmsImport {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const sep = detectSeparator(lines);
  const rows = lines.map((raw, i) => ({ line: i + 1, raw, cells: splitRow(raw, sep) })).filter((r) => r.raw.trim());
  const recipients: SmsRecipient[] = [];
  const rejected: SmsImport['rejected'] = [];
  if (rows.length === 0) return { recipients, rejected, columns: [] };
  const header = rows[0]!.cells.map((c) => c.trim().toLowerCase());
  let phoneCol = header.findIndex((h) => PHONE_HEADERS.includes(h));
  let nameCol: number;
  let columns: string[];
  if (phoneCol >= 0) {
    columns = rows[0]!.cells.map((c) => c.trim());
    nameCol = header.findIndex((h) => NAME_HEADERS.includes(h));
    rows.shift();
  } else {
    const first = rows[0]!.cells;
    phoneCol = Math.max(0, first.findIndex((c) => normalizePhone(c) !== null));
    nameCol = first.findIndex((c) => c.trim().length > 0 && normalizePhone(c) === null);
    const width = Math.max(...rows.map((r) => r.cells.length));
    columns = Array.from({ length: width }, (_, i) => (i === phoneCol ? 'numero' : i === nameCol ? 'nimi' : `sarake${i + 1}`));
  }
  const seen = new Set<string>();
  for (const { line, raw, cells } of rows) {
    const phone = normalizePhone(cells[phoneCol] ?? '');
    if (!phone) {
      rejected.push({ line, text: raw.trim(), reason: 'Numero puuttuu tai ei kelpaa' });
      continue;
    }
    if (seen.has(phone)) {
      rejected.push({ line, text: raw.trim(), reason: 'Sama numero on jo listalla' });
      continue;
    }
    seen.add(phone);
    const fields: Record<string, string> = {};
    columns.forEach((col, c) => {
      if (col) fields[col] = (cells[c] ?? '').trim();
    });
    const name = nameCol >= 0 ? (cells[nameCol] ?? '').trim() : '';
    fields.nimi = name;
    fields.numero = formatPhone(phone);
    recipients.push({ phone, name, fields });
  }
  return { recipients, rejected, columns };
}

/** Numbers typed by hand: separated by commas, semicolons or new lines. */
export function parseNumbers(text: string): SmsImport {
  const recipients: SmsRecipient[] = [];
  const rejected: SmsImport['rejected'] = [];
  const seen = new Set<string>();
  text
    .split(/[,;\n\r]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((part, i) => {
      const phone = normalizePhone(part);
      if (!phone) return void rejected.push({ line: i + 1, text: part, reason: 'Numero ei kelpaa' });
      if (seen.has(phone)) return;
      seen.add(phone);
      recipients.push({ phone, name: '', fields: { nimi: '', numero: formatPhone(phone) } });
    });
  return { recipients, rejected, columns: ['numero'] };
}

// --- The admin's views ----------------------------------------------------------------------------------

export type SmsBatchStatus = 'scheduled' | 'missed' | 'sending' | 'paused' | 'done' | 'cancelled';
export type SmsMessageStatus = 'scheduled' | 'pending' | 'sending' | 'sent' | 'delivered' | 'failed' | 'cancelled';

export interface SmsBatchView {
  id: number;
  title: string;
  createdAt: number;
  scheduledAt: number | null;
  status: SmsBatchStatus;
  createdBy: string | null;
  total: number;
  counts: Partial<Record<SmsMessageStatus, number>>;
  /** The first message (the list shows its beginning). */
  sample: string;
}
export interface SmsMessageView {
  id: number;
  phone: string;
  name: string;
  text: string;
  status: SmsMessageStatus;
  error: string | null;
  sentAt: number | null;
}
export interface SmsTemplateView {
  id: number;
  name: string;
  text: string;
}
export interface SmsGroupView {
  id: number;
  name: string;
  members: { name: string; phone: string }[];
}
